import React, { useEffect } from "react";
import { TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useCustomParams } from "hooks/useCustomParams";
import ExportBSLReportListScreen from "./ExportBSLReportListScreen";

const DATE_RANGE_OPTIONS = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Custom", value: "custom" },
];

const computeRangeDates = (range, customFrom, customTo) => {
  switch (range) {
    case "today":
      return {
        fromDate: moment().format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "last7days":
      return {
        fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "lastMonth":
      return {
        fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "all":
      return { fromDate: "", toDate: "" };
    case "custom":
      return { fromDate: customFrom || "", toDate: customTo || "" };
    default:
      return { fromDate: "", toDate: "" };
  }
};

const BSLReportFilter = ({ disabled = false }) => {
  const { params, appendParams } = useCustomParams();

  const timeRange = params.timeRange;
  const customFrom = params.fromDate || "";
  const customTo = params.toDate || "";

  useEffect(() => {
    if (!timeRange) {
      appendParams({ timeRange: "all", fromDate: "", toDate: "" });
    }
  }, [timeRange, appendParams]);

  const handleRangeChange = (newRange) => {
    if (newRange === "custom") {
      appendParams({ timeRange: "custom" });
      return;
    }
    const { fromDate, toDate } = computeRangeDates(newRange);
    appendParams({ timeRange: newRange, fromDate, toDate });
  };

  const filters = {
    keyword: params.search || "",
    fromDate: customFrom,
    toDate: customTo,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TableFilter
        searchLabel="Branch Name"
        disabled={disabled}
        initialFilters={3}
        actions={<ExportBSLReportListScreen filters={filters} disabled={disabled} />}
      >
        <BaseSelect
          fullWidth
          label="Date Range"
          value={timeRange || "all"}
          onChange={(e) => handleRangeChange(e.target.value)}
          options={DATE_RANGE_OPTIONS}
          getOptionLabel={(opt) => opt?.label || ""}
          getOptionValue={(opt) => opt?.value || ""}
          size="small"
        />

        {timeRange === "custom" && (
          <>
            <DatePicker
              label="From Date"
              value={customFrom ? moment(customFrom) : null}
              onChange={(val) =>
                appendParams({
                  fromDate: val ? val.format("YYYY-MM-DD") : "",
                })
              }
              renderInput={(p) => <TextField {...p} size="small" fullWidth />}
              format="MM/DD/YYYY"
              maxDate={customTo ? moment(customTo) : moment()}
              disableFuture
            />
            <DatePicker
              label="To Date"
              value={customTo ? moment(customTo) : null}
              onChange={(val) =>
                appendParams({
                  toDate: val ? val.format("YYYY-MM-DD") : "",
                })
              }
              renderInput={(p) => <TextField {...p} size="small" fullWidth />}
              format="MM/DD/YYYY"
              minDate={customFrom ? moment(customFrom) : undefined}
              maxDate={moment()}
              disableFuture
            />
          </>
        )}
      </TableFilter>
    </LocalizationProvider>
  );
};

export default BSLReportFilter;